import { html, unsafeCSS } from 'lit';
import { query, customElement } from 'lit/decorators.js';
import { styles as specificAccordionStyles } from '../view/view.scss';
import { WEB_COMPONENT_POLICY_PANEL_BLOCKLIST_RULES_GENERAL } from '../../../../../../../webcomponents.constants';
import { translateFn } from '../../../../../../../utils/translator.helper';
import { GzFormFieldWebComponent } from '../../../../../../../ui/gzForm/formField';
import { GzToggleWebComponent } from '../../../../../../../ui/gzToggle';
import { FormGroup } from '../../../../../../../ui/gzForm/formGroup/form-group';
import { ModuleId, SectionId } from '@nirvana/shared/dtos/types/policies/common';
import { GzPolicyPanelAccordionTemplateWebComponent } from '../../../../../../common/policyPanel/uiSettings/accordionTemplate';
import { GzCheckboxWebComponent } from '../../../../../../../ui/gzCheckbox';
import _ from 'lodash';
import { FormGroupValue } from '../../../../../../../ui/gzForm/formGroup/types/form-group-value';
import { toggleDisabled, toggleVisibility } from '../../../../../../../utils/htmlElement.utils';
import { PolicyPanelManager } from '../../../../PolicyPanelManager';
import deepmerge from 'deepmerge';
import {
  getBlocklistRulesDefaultValue,
  getBlocklistRulesSearchableKeywords,
} from '../../../../utils/uiSettings/blocklistRules/blocklist-rules.values';
import { USE_EXT_JS_VERSION } from '../../../../config/config';
import {
  BlocklistRulesFormValue,
  BlocklistRulesSearchableKeywords,
} from '../../../../utils/uiSettings/blocklistRules/blocklist-rules.types';
import { ButtonColor } from '../../../../../../../ui/gzIconButton/types';
import { GzTooltip } from '../../../../../../../ui/gzTooltip';
import { RootState } from '@nirvana/redux/store';

@customElement(WEB_COMPONENT_POLICY_PANEL_BLOCKLIST_RULES_GENERAL)
export class GzBlocklistRulesGeneralWebComponent extends GzPolicyPanelAccordionTemplateWebComponent<
  BlocklistRulesFormValue,
  BlocklistRulesSearchableKeywords
> {
  /**
   * Path for BLocklist Rules module. (Section is missing)
   */
  protected path = {
    moduleId: ModuleId.BLOCKLIST_RULES,
    sectionId: SectionId.NONE,
  };

  private tooltipInstance: GzTooltip;

  /**
   * Searchable keywords for BLocklist Rules section.
   */
  protected searchableKeywords = getBlocklistRulesSearchableKeywords();

  @query('#blocklist-rules-toggle')
  private blocklistRulesToggle: GzFormFieldWebComponent<GzToggleWebComponent, boolean>;

  @query('#blocklist-rules-content')
  private blocklistRulesContent: HTMLElement;

  @query('#application-hash-children')
  private applicationHashChildren: HTMLElement;


  @query('#script-files-warning')
  private blocklistRulesPerformanceWarningText: HTMLElement;

  @query('#firewall-disabled-warning')
  private firewallDisabledWarning: HTMLElement;

  @query('#blocklist-rules-application-hash')
  private blocklistRulesApplicationHash: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;

  @query('#blocklist-rules-dll-files')
  private blocklistRulesDllFiles: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;

  @query('#blocklist-rules-script-files')
  private blocklistRulesScriptFiles: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;

  @query('#blocklist-rules-application-path')
  private blocklistRulesApplicationPath: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;

  @query('#blocklist-rules-network-connection')
  private blocklistRulesNetworkConnection: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;

  private scriptFilesChecked = false;
  private dllFilesChecked = false;
  private showNetworkConnection = true;
  private showApplicationPath = false;

  /**
   * Current accordion styles merged with template styles.
   */
  static get styles() {
    return [...GzPolicyPanelAccordionTemplateWebComponent.styles, unsafeCSS(`${specificAccordionStyles}`)];
  }

  /**
   * Render specific title.
   */
  protected renderTitle() {
    return html` <gz-form-field id="blocklist-rules-toggle" status-field>
      <gz-toggle
        class="content"
        searchable-label="${this.searchableKeywords[BlocklistRulesSearchableKeywords.BlocklistRules].label}"
        gz-form-input
        @toggleChange="${this.onToggleChange}"
        ><span
          >${this.searchableKeywords[BlocklistRulesSearchableKeywords.BlocklistRules].translatedLabel}</span
        ></gz-toggle
      >
    </gz-form-field>`;
  }

  /**
   * When store state changed and data is loaded for current path, value needs to be received.
   * @param rootState
   */
  public async stateChanged(rootState: RootState) {

    const currentContentControlState = PolicyPanelManager.getInstance().getPolicy('uiSettings.contentControl'),
      contentControlWebState =  currentContentControlState?.application?.enable ?? false,
      contentControlTrafficState =  currentContentControlState?.traffic?.enable ?? false,
      currentFirewallState = PolicyPanelManager.getInstance().getPolicy('uiSettings.firewall'),
      hasFirewallFeature = PolicyPanelManager.getInstance().isFirewallModuleAvailable(),
      hasContentControlFeature = PolicyPanelManager.getInstance().isContentControlWebSectionAvailable()
    ;

    this.showNetworkConnection = hasFirewallFeature && (currentFirewallState?.settings?.enable ?? false);
    this.showApplicationPath = hasContentControlFeature && contentControlWebState && contentControlTrafficState;

    await super.stateChanged(rootState);
  }

  /**
   * Render specific info text.
   * @protected
   */
  protected renderInfoText() {
    return html` <gz-text fontSize="12" fontWeight="400" class="infoText" id="blocklist-rules-description"
    >${translateFn(
      'Customize your blocklist settings in the Blocklist section of GravityZone to prevent specific files, applications, or network connections from running or accessing your system. The Blocklist feature enforces a defined rule set, applying the rules to your endpoints when enabled.'
    )}
    </gz-text>`;
  }

  /**
   * Render specific content.
   */
  protected renderContent() {
    return html`
      <div id="blocklist-rules-content">
        <div class="form-section" id="blocklist-rules">
          <div class="field-with-icon-row">
            <div>
              <gz-form-field id="blocklist-rules-application-hash">
                <gz-checkbox
                  searchable-label="${this.searchableKeywords[BlocklistRulesSearchableKeywords.ApplicationHash].label}"
                  gz-form-input
                  name="uiSettings.blocklistRules.categories.applicationHash"
                  @change="${this.onApplicationHashChange}"
                  label="${this.searchableKeywords[BlocklistRulesSearchableKeywords.ApplicationHash].translatedLabel}"
                ></gz-checkbox>
                <gz-input-error></gz-input-error>
              </gz-form-field>
            </div>
            <div id="blocklist-rules-application-hash-icons">
              <gz-icon name="ds-icon-windows" id="blocklist-rules-application-hash-icon-windows" color="${ButtonColor.SECONDARY}"></gz-icon>
              <gz-icon name="ds-icon-MacOS" id="blocklist-rules-application-hash-icon-mac" color="${ButtonColor.SECONDARY}"></gz-icon>
              <gz-icon name="ds-icon-linux" id="blocklist-rules-application-hash-icon-linux" color="${ButtonColor.SECONDARY}"></gz-icon>
            </div>
          </div>

          <div class="extra-margin" id="application-hash-children">
            <div class="field-with-icon-row">
              <div>
                <gz-form-field id="blocklist-rules-dll-files">
                  <gz-checkbox
                    searchable-label="${this.searchableKeywords[BlocklistRulesSearchableKeywords.DllFiles].label}"
                    gz-form-input
                    name="uiSettings.blocklistRules.categories.dllFiles"
                    label="${this.searchableKeywords[BlocklistRulesSearchableKeywords.DllFiles].translatedLabel}"
                    @change="${this.onDllFilesChange}"
                  ></gz-checkbox>
                  <gz-input-error></gz-input-error>
                </gz-form-field>
              </div>
              <div class="less-margin" id="blocklist-rules-dll-files-icons">
                <gz-icon name="ds-icon-windows" id="blocklist-rules-dll-files-icon-windows" color="${ButtonColor.SECONDARY}"></gz-icon>
                <gz-icon name="ds-icon-MacOS" id="blocklist-rules-dll-files-icon-mac" color="${ButtonColor.SECONDARY}"></gz-icon>
                <gz-icon name="ds-icon-linux" id="blocklist-rules-dll-files-icon-linux" color="${ButtonColor.SECONDARY}"></gz-icon>
              </div>
            </div>

            <div class="field-with-icon-row">
              <div>
                <gz-form-field id="blocklist-rules-script-files">
                  <gz-checkbox
                    searchable-label="${this.searchableKeywords[BlocklistRulesSearchableKeywords.ScriptFiles].label}"
                    gz-form-input
                    name="uiSettings.blocklistRules.categories.scriptFiles"
                    label="${this.searchableKeywords[BlocklistRulesSearchableKeywords.ScriptFiles].translatedLabel}"
                    @change="${this.onScriptFilesChange}"
                  ></gz-checkbox>
                  <gz-input-error></gz-input-error>
                </gz-form-field>
              </div>
              <div class="less-margin" id="blocklist-rules-script-files-icons">
                <gz-icon name="ds-icon-windows" id="blocklist-rules-script-files-icons-windows" color="${ButtonColor.SECONDARY}"></gz-icon>
                <gz-icon name="ds-icon-MacOS" id="blocklist-rules-script-files-icons-mac" color="${ButtonColor.SECONDARY}"></gz-icon>
                <gz-icon name="ds-icon-linux" id="blocklist-rules-script-files-icons-linux" color="${ButtonColor.SECONDARY}"></gz-icon>
              </div>
            </div>
            <gz-text
              id="performance-warning-text"
              class="blocklist-rules-text mb-1"
              fontSize="12"
              fontWeight="400"
              class="infoText"
              >${translateFn('Enabling script file blocking can disrupt applications that rely on script execution. ')}
            </gz-text>
            <gz-info-box id="script-files-warning" class="hidden pl-0" severity="warning">
              ${translateFn(
                'Testing is recommended before applying this setting in production, as it may impact application performance and functionality.'
              )}
            </gz-info-box>
          </div>

          <div>
            <div id="application-path-container" class="${this.showApplicationPath ? '' : 'disabled'}">
              <div class="field-with-icon-row">
                <div>
                  <gz-form-field id="blocklist-rules-application-path" class="padding-top-after-info-text">
                    <gz-checkbox
                      searchable-label="${this.searchableKeywords[BlocklistRulesSearchableKeywords.ApplicationPath]
                        .label}"
                      gz-form-input
                      name="uiSettings.blocklistRules.categories.applicationPath"
                      label="${this.searchableKeywords[BlocklistRulesSearchableKeywords.ApplicationPath]
                        .translatedLabel}"
                    ></gz-checkbox>
                    <gz-input-error></gz-input-error>
                  </gz-form-field>
                </div>
                <div class="padding-top-after-info-text" id="blocklist-rules-application-path-icons">
                  <gz-icon name="ds-icon-windows" id="blocklist-rules-application-path-icon-windows" color="${ButtonColor.SECONDARY}"></gz-icon>
                  <gz-icon name="ds-icon-MacOS" id="blocklist-rules-application-path-icon-mac" color="${ButtonColor.SECONDARY}"></gz-icon>
                  <gz-icon name="ds-icon-linux" id="blocklist-rules-application-path-icon-linux" color="${ButtonColor.SECONDARY}"></gz-icon>
                </div>
              </div>
              <gz-text
                class="blocklist-rules-text mb-1"
                id="blocklist-rules-application-path-info-text"
                fontSize="12"
                fontWeight="400"
                class="infoText"
                >${translateFn(
                  'Block applications from specified file paths. These rules will not take effect if the Content Control module is not installed or Application Blacklisting disabled.'
                )}
              </gz-text>
            </div>
            <gz-info-box id="appllication-blacklisting-disabled-warning" class="pl-0 ${this.showApplicationPath ? 'hidden' : ''}" severity="warning">
              ${translateFn('This option requires Application Blacklisting to be enabled in Network Protection > Content Control.')}
            </gz-info-box>
          </div>

          <div>
            <div id="network-connection-container" class="${this.showNetworkConnection ? '' : 'disabled'}">
              <div class="field-with-icon-row">
                <div>
                  <gz-form-field id="blocklist-rules-network-connection" class="padding-top-after-info-text">
                    <gz-checkbox
                      searchable-label="${this.searchableKeywords[BlocklistRulesSearchableKeywords.NetworkConnection]
                        .label}"
                      gz-form-input
                      name="uiSettings.blocklistRules.categories.networkConnection"
                      label="${this.searchableKeywords[BlocklistRulesSearchableKeywords.NetworkConnection]
                        .translatedLabel}"
                    ></gz-checkbox>
                    <gz-input-error></gz-input-error>
                  </gz-form-field>
                </div>
                <div class="padding-top-after-info-text" id="blocklist-rules-network-icons">
                  <gz-icon name="ds-icon-windows" id="blocklist-rules-network-icon-windows" color="${ButtonColor.SECONDARY}"></gz-icon>
                </div>
              </div>
              <gz-text
                class="blocklist-rules-text mb-1"
                id="blocklist-rules-network-connection-info-text"
                fontSize="12"
                fontWeight="400"
                class="infoText"
                >${translateFn(
                  'Enhance security by blocking connections to or from specific IPs, ports, or MAC addresses. These rules will not take effect if the Firewall module is not installed or disabled.'
                )}
              </gz-text>
            </div>

            <gz-info-box id="firewall-disabled-warning" class="pl-0 ${this.showNetworkConnection ? 'hidden' : ''}" severity="warning">
              ${translateFn('This option requires the Firewall module to be enabled in the policy.')}
            </gz-info-box>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Get data from manager and set missing fields.
   * Fields used in this section:
   * - uiSettings.blocklistRules
   */
  protected getValueFromManager(): BlocklistRulesFormValue {
    // get default value
    const defaultValue = {
      uiSettings: {
        [ModuleId.BLOCKLIST_RULES]: getBlocklistRulesDefaultValue(),
      },
    };

    // get value from manager
    const value = {
      uiSettings: {
        [ModuleId.BLOCKLIST_RULES]: PolicyPanelManager.getInstance().getPolicy('uiSettings.blocklistRules'),
      },
    };

    // merge received value with default one
    return deepmerge(defaultValue, value);
  }

  /**
   * Create form group.
   */
  protected createFormGroup() {
    this.formGroup = FormGroup.build({
      uiSettings: {
        blocklistRules: {
          enabled: this.blocklistRulesToggle,
          categories: {
            applicationHash: this.blocklistRulesApplicationHash,
            dllFiles: this.blocklistRulesDllFiles,
            scriptFiles: this.blocklistRulesScriptFiles,
            applicationPath: this.blocklistRulesApplicationPath,
            networkConnection: this.blocklistRulesNetworkConnection
          }
        },
      },
    });
  }

  /**
   * Set form group value.
   */
  protected setFormData() {
    if (!this.valueT) {
      return;
    }

    // set form group value
    this.formGroup.value = {
      uiSettings: {
        blocklistRules: {
          enabled: this.valueT.uiSettings.blocklistRules.enabled,
          categories: {
            applicationHash: this.valueT.uiSettings.blocklistRules.categories.applicationHash,
            dllFiles: this.valueT.uiSettings.blocklistRules.categories.dllFiles,
            scriptFiles: this.valueT.uiSettings.blocklistRules.categories.scriptFiles,
            applicationPath: this.valueT.uiSettings.blocklistRules.categories.applicationPath,
            networkConnection: this.valueT.uiSettings.blocklistRules.categories.networkConnection,
          }
        },
      },
    };

    this.dllFilesChecked = this.valueT.uiSettings.blocklistRules.categories.dllFiles;
    this.scriptFilesChecked = this.valueT.uiSettings.blocklistRules.categories.scriptFiles;
  }

  /**
   * Set validators for form fields.
   */
  protected setValidators() {
    // add event listener for setting validators
  }

  /**
   * Get form group value according to backend format.
   */
  protected getFormGroupValue(): FormGroupValue | null {
    if (!this.formGroup) {
      return null;
    }

    const value = _.cloneDeep(this.formGroup.value);

    if (USE_EXT_JS_VERSION) {
      return _.get(value, ['uiSettings', 'blocklistRules'], {}) as FormGroupValue;
    }

    return value;
  }

  /**
   * Enable/Disable module.
   * @param event
   */
  onToggleChange(event: CustomEvent) {
    toggleDisabled(this.blocklistRulesContent, !event.detail.checked);
  }

  /**
   *
   * @param status
   */
  togglePerformanceWarningText(status) {
    toggleVisibility(this.blocklistRulesPerformanceWarningText, status);
  }

  /**
   *
   * @param event
   */
  onDllFilesChange(event: CustomEvent) {
    this.dllFilesChecked = event.detail.checked;
    this.togglePerformanceWarningText(this.dllFilesChecked && this.scriptFilesChecked);
  }

  /**
   *
   * @param event
   */
  onScriptFilesChange(event: CustomEvent) {
    this.scriptFilesChecked = event.detail.checked;
    this.togglePerformanceWarningText(this.dllFilesChecked && this.scriptFilesChecked);
  }

  /**
   * show/hide warning message.
   * @param event
   */
  onApplicationHashChange(event: CustomEvent) {
    toggleDisabled(this.applicationHashChildren, !event.detail.checked);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    WEB_COMPONENT_POLICY_PANEL_BLOCKLIST_RULES_GENERAL: GzBlocklistRulesGeneralWebComponent;
  }
}
